import React, { useEffect, useMemo, useState } from "react";
import { hrApi } from "../../services/hrApi";
import HROrgChartNode from "./HROrgChartNode";

function buildTree(rows) {
  const byPosition = new Map();

  for (const row of rows || []) {
    const key = row.position_id;

    if (!byPosition.has(key)) {
      byPosition.set(key, {
        position_id: row.position_id,
        position_code: row.position_code,
        position_title: row.position_title,
        department_id: row.department_id,
        department_code: row.department_code,
        department_name: row.department_name,
        reports_to_position_id: row.reports_to_position_id,
        reports_to_position_code: row.reports_to_position_code,
        reports_to_position_title: row.reports_to_position_title,
        position_mailbox: row.position_mailbox,
        planned_headcount: row.planned_headcount,
        is_leadership: Number(row.is_leadership) === 1,
        people: [],
        children: [],
      });
    }

    const node = byPosition.get(key);

    if (row.person_id) {
      node.people.push({
        person_id: row.person_id,
        employee_code: row.employee_code,
        full_name: row.full_name,
        display_name: row.display_name,
        email_work: row.email_work,
        is_placeholder: Number(row.is_placeholder) === 1,
        is_primary: Number(row.is_primary) === 1,
        assignment_type: row.assignment_type,
        allocation_percent: Number(row.allocation_percent || 0),
        assigned_from: row.assigned_from,
        assigned_to: row.assigned_to,
      });
    }
  }

  const nodes = Array.from(byPosition.values());
  const nodeMap = new Map(nodes.map((n) => [n.position_id, n]));
  const roots = [];

  for (const node of nodes) {
    if (node.reports_to_position_id && nodeMap.has(node.reports_to_position_id)) {
      nodeMap.get(node.reports_to_position_id).children.push(node);
    } else {
      roots.push(node);
    }
  }

  const sortNode = (node) => {
    node.children.sort((a, b) =>
      String(a.position_title || "").localeCompare(String(b.position_title || ""))
    );
    node.children.forEach(sortNode);
  };

  const enrichNode = (node) => {
  const directPeopleCount = node.people?.length || 0;
  const directChildrenCount = node.children?.length || 0;

  let descendantPositionsCount = 0;
  let descendantPeopleCount = 0;

  for (const child of node.children || []) {
    enrichNode(child);

    descendantPositionsCount += 1 + (child.descendantPositionsCount || 0);
    descendantPeopleCount += (child.directPeopleCount || 0) + (child.descendantPeopleCount || 0);
  }

  node.directPeopleCount = directPeopleCount;
  node.directChildrenCount = directChildrenCount;
  node.descendantPositionsCount = descendantPositionsCount;
  node.descendantPeopleCount = descendantPeopleCount;

  node.totalSubtreePositionsCount = 1 + descendantPositionsCount;
  node.totalSubtreePeopleCount = directPeopleCount + descendantPeopleCount;
};

  roots.forEach(sortNode);
  roots.forEach(enrichNode);

  return roots.sort((a, b) => {
    if (["POS_GM", "CEO"].includes(a.position_code)) return -1;
    if (["POS_GM", "CEO"].includes(b.position_code)) return 1;
    return String(a.position_title || "").localeCompare(String(b.position_title || ""));
  });

  
}

function flattenNodes(nodes, acc = []) {
  for (const node of nodes) {
    acc.push(node);
    if (node.children?.length) flattenNodes(node.children, acc);
  }
  return acc;
}

function findNodeById(nodes, id) {
  for (const node of nodes) {
    if (node.position_id === id) return node;
    const child = findNodeById(node.children || [], id);
    if (child) return child;
  }
  return null;
}

function findPathToNode(nodes, targetId, path = []) {
  for (const node of nodes) {
    const nextPath = [...path, node];
    if (node.position_id === targetId) return nextPath;

    if (node.children?.length) {
      const found = findPathToNode(node.children, targetId, nextPath);
      if (found) return found;
    }
  }
  return null;
}

function collectReachableIds(nodes, set = new Set()) {
  for (const node of nodes) {
    set.add(node.position_id);
    if (node.children?.length) collectReachableIds(node.children, set);
  }
  return set;
}

function LevelRow({ nodes, selectedId, onSelect, globalExpanded }) {
  if (!nodes?.length) return null;

  return (
    <div className="flex flex-col items-center">
      <div className="w-full flex justify-center">
        <div className="flex flex-wrap justify-center items-start gap-x-6 gap-y-8">
          {nodes.map((node) => (
            <div key={node.position_id} className="flex flex-col items-center">
              <div
                className={`rounded-2xl transition-all ${
                  selectedId === node.position_id ? "ring-2 ring-blue-400/70" : ""
                }`}
              >
                <HROrgChartNode
                  node={node}
                  compact
                  expanded={globalExpanded}
                  onSelect={() => onSelect(node)}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function OrgDrawer({
  node,
  onClose,
  editMode,
  setEditMode,
  positionsCatalog,
  peopleCatalog,
  assignments,
  assignForm,
  setAssignForm,
  editForm,
  setEditForm,
  parentForm,
  setParentForm,
  childForm,
  setChildForm,
  handleSaveEdit,
  handleSaveParent,
  handleCreateChild,
  handleCreateAssignment,
  handleDeleteAssignment,
  saving,
}) {
  if (!node) return null;

  return (
    <div className="fixed inset-y-0 right-0 w-[420px] max-w-[90vw] bg-white dark:bg-neutral-950 border-l border-gray-200 dark:border-neutral-800 shadow-2xl z-50">
      <div className="p-5 border-b border-gray-200 dark:border-neutral-800 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-xl font-semibold">{node.position_title}</h3>
          <div className="text-sm text-gray-500 mt-1">{node.position_code}</div>
        </div>

        <button
          onClick={onClose}
          className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-neutral-700 text-sm"
        >
          Close
        </button>
      </div>

      <div className="p-5 space-y-5 overflow-y-auto h-[calc(100%-73px)]">
        <div>
          <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">
            Position
          </div>
          <div className="rounded-xl border border-gray-200 dark:border-neutral-800 p-4 space-y-2">
            <div><span className="text-gray-400">Department:</span> {node.department_name || "-"}</div>
            <div><span className="text-gray-400">Mailbox:</span> {node.position_mailbox || "-"}</div>
            <div><span className="text-gray-400">Planned headcount:</span> {node.planned_headcount || 1}</div>
            <div><span className="text-gray-400">Leadership:</span> {node.is_leadership ? "Yes" : "No"}</div>
            <div><span className="text-gray-400">Reports to:</span> {node.reports_to_position_title || "-"}</div>
          </div>
        </div>

        <div>
          <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">
            Assigned people
          </div>
          <div className="rounded-xl border border-gray-200 dark:border-neutral-800 p-4 space-y-3">
            {node.people?.length ? (
              node.people.map((person) => (
                <div
                  key={`${node.position_id}-${person.person_id}-${person.assignment_type}`}
                  className="rounded-lg border border-gray-200 dark:border-neutral-800 p-3"
                >
                  <div className="font-medium">{person.display_name || person.full_name}</div>
                  <div className="text-sm text-gray-500">{person.email_work || "-"}</div>
                  <div className="text-xs text-gray-400 mt-1">
                    {person.is_primary ? "Primary" : person.assignment_type || "Secondary"} {" • "}
                    {person.is_placeholder ? "Placeholder" : "Real person"}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-sm text-gray-500">No assigned people.</div>
            )}
          </div>
        </div>

        <div>
          <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">
            Actions
          </div>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setEditMode("edit")}
              className="px-3 py-2 rounded-xl border border-gray-300 dark:border-neutral-700 text-sm"
            >
              Edit position
            </button>

            <button
              onClick={() => setEditMode("parent")}
              className="px-3 py-2 rounded-xl border border-gray-300 dark:border-neutral-700 text-sm"
            >
              Change parent
            </button>

            <button
  onClick={() => setEditMode("assign")}
  className="px-3 py-2 rounded-xl border border-gray-300 dark:border-neutral-700 text-sm"
>
  Assign person
</button>

            <button
              onClick={() => setEditMode("child")}
              className="px-3 py-2 rounded-xl border border-gray-300 dark:border-neutral-700 text-sm"
            >
              Add child
            </button>
          </div>
        </div>

        {editMode === "assign" && (
  <div className="mt-5 rounded-xl border border-gray-200 dark:border-neutral-800 p-4 space-y-3">
    <div className="font-medium">Assign person</div>

    <select
  className="w-full px-3 py-2 rounded-lg border border-gray-300 
             bg-white text-black
             dark:bg-neutral-900 dark:text-white dark:border-neutral-700"
  value={assignForm.person_id}
  onChange={(e) => setAssignForm((s) => ({ ...s, person_id: e.target.value }))}
>
  <option value="" className="bg-white text-black dark:bg-neutral-900 dark:text-white">
    Select person
  </option>

  {peopleCatalog.map((p) => (
    <option
      key={p.id}
      value={p.id}
      className="bg-white text-black dark:bg-neutral-900 dark:text-white"
    >
      {(p.display_name || p.full_name || `${p.first_name || ""} ${p.last_name || ""}`).trim()} ({p.employee_code || "no-code"})
    </option>
  ))}
</select>

    <div>
  <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">
    Position assignments
  </div>
  <div className="rounded-xl border border-gray-200 dark:border-neutral-800 p-4 space-y-3">
    {assignments.length ? (
      assignments.map((a) => (
        <div
          key={a.id}
          className="rounded-lg border border-gray-200 dark:border-neutral-800 p-3 flex items-start justify-between gap-3"
        >
          <div>
            <div className="font-medium">{a.display_name || a.full_name}</div>
            <div className="text-sm text-gray-500">{a.email_work || "-"}</div>
            <div className="text-xs text-gray-400 mt-1">
              {a.assignment_type} • {Number(a.is_primary) === 1 ? "primary" : "not primary"} • {a.allocation_percent}%
            </div>
          </div>

          <button
            onClick={() => handleDeleteAssignment(a.id)}
            className="px-2 py-1 rounded-lg border text-xs"
          >
            Remove
          </button>
        </div>
      ))
    ) : (
      <div className="text-sm text-gray-500">No assignments found.</div>
    )}
  </div>
</div>

    <select
  className="w-full px-3 py-2 rounded-lg border border-gray-300 
             bg-white text-black
             dark:bg-neutral-900 dark:text-white dark:border-neutral-700"
  value={assignForm.assignment_type}
  onChange={(e) =>
    setAssignForm((s) => ({ ...s, assignment_type: e.target.value }))
  }
>
  <option className="bg-white text-black dark:bg-neutral-900 dark:text-white" value="primary">
    Primary
  </option>
  <option className="bg-white text-black dark:bg-neutral-900 dark:text-white" value="secondary">
    Secondary
  </option>
  <option className="bg-white text-black dark:bg-neutral-900 dark:text-white" value="temporary">
    Temporary
  </option>
  <option className="bg-white text-black dark:bg-neutral-900 dark:text-white" value="acting">
    Acting
  </option>
</select>

    <label className="flex items-center gap-2 text-sm">
      <input
        type="checkbox"
        checked={Number(assignForm.is_primary) === 1}
        onChange={(e) => setAssignForm((s) => ({ ...s, is_primary: e.target.checked ? 1 : 0 }))}
      />
      Primary assignment
    </label>

    <input
      type="number"
      min="1"
      max="100"
      className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-neutral-700 bg-transparent"
      placeholder="Allocation percent"
      value={assignForm.allocation_percent}
      onChange={(e) => setAssignForm((s) => ({ ...s, allocation_percent: e.target.value }))}
    />

    <input
      type="date"
      className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-neutral-700 bg-transparent"
      value={assignForm.assigned_from}
      onChange={(e) => setAssignForm((s) => ({ ...s, assigned_from: e.target.value }))}
    />

    <input
      type="date"
      className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-neutral-700 bg-transparent"
      value={assignForm.assigned_to}
      onChange={(e) => setAssignForm((s) => ({ ...s, assigned_to: e.target.value }))}
    />

    <textarea
      className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-neutral-700 bg-transparent"
      rows={3}
      placeholder="Notes"
      value={assignForm.notes}
      onChange={(e) => setAssignForm((s) => ({ ...s, notes: e.target.value }))}
    />

    <div className="flex gap-2">
      <button
        onClick={handleCreateAssignment}
        disabled={saving || !assignForm.person_id}
        className="px-3 py-2 rounded-lg border"
      >
        Assign
      </button>
      <button
        onClick={() => setEditMode(null)}
        className="px-3 py-2 rounded-lg border"
      >
        Cancel
      </button>
    </div>
  </div>
)}

        {editMode === "parent" && (
          <div className="mt-5 rounded-xl border border-gray-200 dark:border-neutral-800 p-4 space-y-3">
            <div className="font-medium">Change parent</div>

            <select
  className="w-full px-3 py-2 rounded-lg border border-gray-300 
             bg-white text-black
             dark:bg-neutral-900 dark:text-white dark:border-neutral-700"
  value={parentForm.reports_to_position_id}
  onChange={(e) =>
    setParentForm((s) => ({
      ...s,
      reports_to_position_id: e.target.value,
    }))
  }
>
  <option value="" className="bg-white text-black dark:bg-neutral-900 dark:text-white">
    No parent
  </option>

  {positionsCatalog
    .filter((p) => p.id !== node.position_id)
    .map((p) => (
      <option
        key={p.id}
        value={p.id}
        className="bg-white text-black dark:bg-neutral-900 dark:text-white"
      >
        {p.title} ({p.code})
      </option>
    ))}
</select>

            <div className="flex gap-2">
              <button onClick={handleSaveParent} disabled={saving} className="px-3 py-2 rounded-lg border">
                Save
              </button>
              <button onClick={() => setEditMode(null)} className="px-3 py-2 rounded-lg border">
                Cancel
              </button>
            </div>
          </div>
        )}

        {editMode === "edit" && (
  <div className="mt-5 rounded-xl border border-gray-200 dark:border-neutral-800 p-4 space-y-3">
    <div className="font-medium">Edit position</div>

    <input
      className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-neutral-700 bg-transparent"
      placeholder="Title"
      value={editForm.title}
      onChange={(e) => setEditForm((s) => ({ ...s, title: e.target.value }))}
    />

    <input
      className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-neutral-700 bg-transparent"
      placeholder="Code"
      value={editForm.code}
      onChange={(e) => setEditForm((s) => ({ ...s, code: e.target.value }))}
    />

    <input
      className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-neutral-700 bg-transparent"
      placeholder="Mailbox"
      value={editForm.position_mailbox}
      onChange={(e) =>
        setEditForm((s) => ({ ...s, position_mailbox: e.target.value }))
      }
    />

    <input
      type="number"
      min="1"
      className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-neutral-700 bg-transparent"
      value={editForm.planned_headcount}
      onChange={(e) =>
        setEditForm((s) => ({ ...s, planned_headcount: e.target.value }))
      }
    />

    <label className="flex items-center gap-2 text-sm">
      <input
        type="checkbox"
        checked={Number(editForm.is_leadership) === 1}
        onChange={(e) =>
          setEditForm((s) => ({ ...s, is_leadership: e.target.checked ? 1 : 0 }))
        }
      />
      Leadership
    </label>

    <div className="flex gap-2">
      <button
        onClick={handleSaveEdit}
        disabled={saving}
        className="px-3 py-2 rounded-lg border"
      >
        Save
      </button>
      <button
        onClick={() => setEditMode(null)}
        className="px-3 py-2 rounded-lg border"
      >
        Cancel
      </button>
    </div>
  </div>
)}

        {editMode === "child" && (
          <div className="mt-5 rounded-xl border border-gray-200 dark:border-neutral-800 p-4 space-y-3">
            <div className="font-medium">Add child position</div>

            <input
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-neutral-700 bg-transparent"
              placeholder="Title"
              value={childForm.title}
              onChange={(e) => setChildForm((s) => ({ ...s, title: e.target.value }))}
            />

            <input
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-neutral-700 bg-transparent"
              placeholder="Code"
              value={childForm.code}
              onChange={(e) => setChildForm((s) => ({ ...s, code: e.target.value }))}
            />

            <input
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-neutral-700 bg-transparent"
              placeholder="Mailbox"
              value={childForm.position_mailbox}
              onChange={(e) => setChildForm((s) => ({ ...s, position_mailbox: e.target.value }))}
            />

            <input
              type="number"
              min="1"
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-neutral-700 bg-transparent"
              value={childForm.planned_headcount}
              onChange={(e) => setChildForm((s) => ({ ...s, planned_headcount: e.target.value }))}
            />

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={Number(childForm.is_leadership) === 1}
                onChange={(e) => setChildForm((s) => ({ ...s, is_leadership: e.target.checked ? 1 : 0 }))}
              />
              Leadership
            </label>

            <div className="flex gap-2">
              <button onClick={handleCreateChild} disabled={saving} className="px-3 py-2 rounded-lg border">
                Create
              </button>
              <button onClick={() => setEditMode(null)} className="px-3 py-2 rounded-lg border">
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function HROrgChartTree({ rows = [], reloadOrgChart }) {
  const tree = useMemo(() => buildTree(rows), [rows]);

  const stats = useMemo(() => {
    const flat = flattenNodes(tree);
    return {
      totalPositions: flat.length,
      filled: flat.filter((n) => n.people.some((p) => !p.is_placeholder)).length,
      placeholderOnly: flat.filter(
        (n) => n.people.length > 0 && n.people.every((p) => p.is_placeholder)
      ).length,
      vacant: flat.filter((n) => n.people.length === 0).length,
    };
  }, [tree]);

  

  const rootNode = tree[0] || null;
  const [activeNodeId, setActiveNodeId] = useState(null);
  const [drawerNode, setDrawerNode] = useState(null);
  const [globalExpanded, setGlobalExpanded] = useState(true);
  const [editMode, setEditMode] = useState(null);
  const [positionsCatalog, setPositionsCatalog] = useState([]);
  const [peopleCatalog, setPeopleCatalog] = useState([]);
  const [saving, setSaving] = useState(false);

  const [editForm, setEditForm] = useState({
    title: "",
    code: "",
    department_id: "",
    position_mailbox: "",
    planned_headcount: 1,
    is_leadership: 0,
  });

  const [assignments, setAssignments] = useState([]);

const [assignForm, setAssignForm] = useState({
  person_id: "",
  assigned_from: "",
  assigned_to: "",
  is_primary: 1,
  allocation_percent: 100,
  assignment_type: "primary",
  notes: "",
});

  const [parentForm, setParentForm] = useState({
    reports_to_position_id: "",
  });

  const [childForm, setChildForm] = useState({
    title: "",
    code: "",
    department_id: "",
    planned_headcount: 1,
    is_leadership: 0,
    position_mailbox: "",
  });

  useEffect(() => {
  if (rootNode && !activeNodeId) {
    setActiveNodeId(rootNode.position_id);
  }
}, [rootNode, activeNodeId]);

const activePath = useMemo(() => {
  if (!rootNode) return [];
  if (!activeNodeId) return [rootNode];
  return findPathToNode([rootNode], activeNodeId) || [rootNode];
}, [rootNode, activeNodeId]);

const activeNode = activePath[activePath.length - 1] || null;

const allRowsByPosition = useMemo(() => {
  const map = new Map();
  for (const row of rows || []) {
    if (!map.has(row.position_id)) {
      map.set(row.position_id, {
        position_id: row.position_id,
        position_code: row.position_code,
        position_title: row.position_title,
        department_name: row.department_name,
        reports_to_position_id: row.reports_to_position_id,
      });
    }
  }
  return Array.from(map.values());
}, [rows]);

const reachableIds = useMemo(() => collectReachableIds(tree), [tree]);

const unlinkedPositions = useMemo(() => {
  return allRowsByPosition.filter((p) => !reachableIds.has(p.position_id));
}, [allRowsByPosition, reachableIds]);

  useEffect(() => {
    async function loadCatalogs() {
      if (!drawerNode) return;

      try {
        const [positionsRes, peopleRes] = await Promise.all([
          hrApi.getPositions(),
          hrApi.getPeople({ limit: 500 }),
        ]);

        setPositionsCatalog(positionsRes?.data?.data || []);
        setPeopleCatalog(peopleRes?.data?.data || []);
      } catch (err) {
        console.error("Failed to load catalogs:", err);
      }
    }

    loadCatalogs();
  }, [drawerNode]);

  useEffect(() => {
  async function loadCatalogs() {
    if (!drawerNode) return;

    try {
      const [positionsRes, peopleRes, assignmentsRes] = await Promise.all([
        hrApi.getPositions(),
        hrApi.getPeople({ limit: 500 }),
        hrApi.getPositionAssignments(drawerNode.position_id),
      ]);

      setPositionsCatalog(positionsRes?.data?.data || []);
      setPeopleCatalog(peopleRes?.data?.data || []);
      setAssignments(assignmentsRes?.data?.data || []);
    } catch (err) {
      console.error("Failed to load catalogs:", err);
    }
  }

  loadCatalogs();
}, [drawerNode]);

  useEffect(() => {
    if (!drawerNode) return;

    if (editMode === "edit") {
      setEditForm({
        title: drawerNode.position_title || "",
        code: drawerNode.position_code || "",
        department_id: drawerNode.department_id || "",
        position_mailbox: drawerNode.position_mailbox || "",
        planned_headcount: drawerNode.planned_headcount || 1,
        is_leadership: drawerNode.is_leadership ? 1 : 0,
      });
    }

    if (editMode === "parent") {
      setParentForm({
        reports_to_position_id: drawerNode.reports_to_position_id || "",
      });
    }

    if (editMode === "child") {
      setChildForm({
        title: "",
        code: "",
        department_id: drawerNode.department_id || "",
        planned_headcount: 1,
        is_leadership: 0,
        position_mailbox: "",
      });
    }
  }, [drawerNode, editMode]);

  async function handleSaveEdit() {
    if (!drawerNode) return;

    try {
      setSaving(true);

      await hrApi.updatePosition(drawerNode.position_id, {
        title: editForm.title,
        code: editForm.code,
        department_id: editForm.department_id || null,
        position_mailbox: editForm.position_mailbox || null,
        planned_headcount: Number(editForm.planned_headcount || 1),
        is_leadership: Number(editForm.is_leadership || 0),
      });

      setEditMode(null);
      await reloadOrgChart?.();
    } catch (err) {
      console.error("Failed to update position:", err);
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveParent() {
    if (!drawerNode) return;

    try {
      setSaving(true);

      await hrApi.updatePosition(drawerNode.position_id, {
        reports_to_position_id: parentForm.reports_to_position_id || null,
      });

      setEditMode(null);
      await reloadOrgChart?.();
    } catch (err) {
      console.error("Failed to change parent:", err);
    } finally {
      setSaving(false);
    }
  }

  async function handleCreateAssignment() {
  if (!drawerNode) return;

  try {
    setSaving(true);

    await hrApi.createPositionAssignment({
      position_id: drawerNode.position_id,
      person_id: assignForm.person_id,
      assigned_from: assignForm.assigned_from || null,
      assigned_to: assignForm.assigned_to || null,
      is_primary: Number(assignForm.is_primary || 0),
      allocation_percent: Number(assignForm.allocation_percent || 100),
      assignment_type: assignForm.assignment_type || "primary",
      notes: assignForm.notes || null,
    });

    setEditMode(null);
    await reloadOrgChart?.();

    const assignmentsRes = await hrApi.getPositionAssignments(drawerNode.position_id);
    setAssignments(assignmentsRes?.data?.data || []);
  } catch (err) {
    console.error("Failed to create assignment:", err);
  } finally {
    setSaving(false);
  }
}

async function handleDeleteAssignment(id) {
  try {
    setSaving(true);
    await hrApi.closePositionAssignment(id);
    await reloadOrgChart?.();

    if (drawerNode) {
      const assignmentsRes = await hrApi.getPositionAssignments(drawerNode.position_id);
      setAssignments(assignmentsRes?.data?.data || []);
    }
  } catch (err) {
    console.error("Failed to delete assignment:", err);
  } finally {
    setSaving(false);
  }
}

  async function handleCreateChild() {
    if (!drawerNode) return;

    try {
      setSaving(true);

      await hrApi.createPosition({
        title: childForm.title,
        code: childForm.code,
        department_id: childForm.department_id || drawerNode.department_id || null,
        reports_to_position_id: drawerNode.position_id,
        planned_headcount: Number(childForm.planned_headcount || 1),
        is_leadership: Number(childForm.is_leadership || 0),
        position_mailbox: childForm.position_mailbox || null,
        active_flag: 1,
      });

      setEditMode(null);
      await reloadOrgChart?.();
    } catch (err) {
      console.error("Failed to create child position:", err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        <div className="rounded-xl border border-gray-200 dark:border-neutral-700 p-4 bg-white dark:bg-neutral-900">
          <div className="text-sm text-gray-500">Total positions</div>
          <div className="text-2xl font-semibold mt-1">{stats.totalPositions}</div>
        </div>
        <div className="rounded-xl border border-gray-200 dark:border-neutral-700 p-4 bg-white dark:bg-neutral-900">
          <div className="text-sm text-gray-500">Filled</div>
          <div className="text-2xl font-semibold mt-1">{stats.filled}</div>
        </div>
        <div className="rounded-xl border border-gray-200 dark:border-neutral-700 p-4 bg-white dark:bg-neutral-900">
          <div className="text-sm text-gray-500">Placeholder only</div>
          <div className="text-2xl font-semibold mt-1">{stats.placeholderOnly}</div>
        </div>
        <div className="rounded-xl border border-gray-200 dark:border-neutral-700 p-4 bg-white dark:bg-neutral-900">
          <div className="text-sm text-gray-500">Vacant</div>
          <div className="text-2xl font-semibold mt-1">{stats.vacant}</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
  <button
    onClick={() => {
      if (rootNode) {
        setActiveNodeId(rootNode.position_id);
      }
      setDrawerNode(null);
      setEditMode(null);
    }}
    className="px-3 py-2 rounded-xl border border-gray-300 bg-white text-black dark:bg-neutral-900 dark:text-white dark:border-neutral-700"
  >
    Executive view
  </button>

  <button
    onClick={() => setGlobalExpanded((v) => !v)}
    className="px-3 py-2 rounded-xl border border-gray-300 bg-white text-black 
               dark:bg-neutral-900 dark:text-white dark:border-neutral-700"
  >
    {globalExpanded ? "Collapse all" : "Expand all"}
  </button>

  {activePath.length > 1 ? (
    <button
      onClick={() => {
        const parent = activePath[activePath.length - 2];
        if (parent) setActiveNodeId(parent.position_id);
      }}
      className="px-3 py-2 rounded-xl border border-gray-300 bg-white text-black dark:bg-neutral-900 dark:text-white dark:border-neutral-700"
    >
      Back to {activePath[activePath.length - 2]?.position_title}
    </button>
  ) : null}
</div>

{unlinkedPositions.length > 0 && (
  <div className="rounded-xl border border-red-200 dark:border-red-900 p-4 bg-white dark:bg-neutral-900">
    <div className="text-sm font-semibold text-red-600 dark:text-red-300 mb-2">
      Unlinked positions
    </div>

    <div className="space-y-2">
      {unlinkedPositions.map((p) => (
        <div
          key={p.position_id}
          className="flex items-center justify-between rounded-lg border border-gray-200 dark:border-neutral-800 px-3 py-2"
        >
          <div>
            <div className="font-medium">{p.position_title}</div>
            <div className="text-xs text-gray-500">
              {p.position_code} • {p.department_name || "No department"}
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
)}

<div className="overflow-x-auto pb-8">
  <div className="min-w-[1200px] space-y-10">
    {activePath.map((node, idx) => {
      const children = node.children || [];
      const isLast = idx === activePath.length - 1;

      return (
        <div key={node.position_id} className="flex flex-col items-center">
          <div
            className={`rounded-2xl transition-all ${
              activeNodeId === node.position_id ? "ring-2 ring-blue-400/70" : ""
            }`}
          >
            <HROrgChartNode
              node={node}
              compact
              expanded={globalExpanded}
              onSelect={() => {
                setActiveNodeId(node.position_id);
                setDrawerNode(node);
                setEditMode(null);
              }}
            />
          </div>

          {children.length > 0 && (
            <>
              <div className="w-px h-6 bg-gray-300 dark:bg-neutral-700" />
              <div className="w-full flex justify-center">
                <div className="border-t border-gray-300 dark:border-neutral-700 w-[1100px]" />
              </div>

              <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mt-4 mb-2">
                {node.position_title} branch
              </div>

              <div className="pt-6">
                <LevelRow
                  nodes={children}
                  selectedId={activeNodeId}
                  globalExpanded={globalExpanded}
                  onSelect={(child) => {
                    setActiveNodeId(child.position_id);
                    setDrawerNode(child);
                    setEditMode(null);
                  }}
                />
              </div>
            </>
          )}

          {!isLast && <div className="h-2" />}
        </div>
      );
    })}
  </div>
</div>



      <OrgDrawer
  node={drawerNode}
  onClose={() => {
    setDrawerNode(null);
    setEditMode(null);
  }}
  editMode={editMode}
  setEditMode={setEditMode}
  positionsCatalog={positionsCatalog}
  peopleCatalog={peopleCatalog}
  assignments={assignments}
  assignForm={assignForm}
  setAssignForm={setAssignForm}
  editForm={editForm}
  setEditForm={setEditForm}
  parentForm={parentForm}
  setParentForm={setParentForm}
  childForm={childForm}
  setChildForm={setChildForm}
  handleSaveEdit={handleSaveEdit}
  handleSaveParent={handleSaveParent}
  handleCreateChild={handleCreateChild}
  handleCreateAssignment={handleCreateAssignment}
  handleDeleteAssignment={handleDeleteAssignment}
  saving={saving}
/>
    </div>
  );
}